import { Clock } from 'lucide-react'
import type { AccountSummary, AccountType } from '@/api/accounts'
import type { EarmarkResponse } from '@/api/goals'
import type { Valuation } from '@/api/valuations'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { WIDE_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { investmentGain, subTypeLabel } from '@/lib/accountDisplay'
import { getDefaultAccountIcon } from '@/lib/accountIconOptions'
import { CategoryIcon } from '@/lib/categoryIcon'
import { daysUntil, nextBillDate } from '@/lib/creditCard'
import { formatShortDate } from '@/lib/formatDate'

const TYPE_LABELS: Record<AccountType, string> = {
  BANK: 'Bank',
  CASH: 'Cash',
  DIGITAL_WALLET: 'Digital wallet',
  INVESTMENT: 'Investment',
  RECEIVABLE: 'Money owed to you',
  PROPERTY: 'Property',
  LOAN: 'Loan',
  CREDIT_CARD: 'Credit card',
  PAYABLE: 'Money you owe',
}

const SHORT_DATE = { day: 'numeric', month: 'short' } as const

function daysLabel(days: number): string {
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  return `in ${days} days`
}

function agoLabel(days: number): string {
  if (days === 0) return 'today'
  if (days === 1) return 'yesterday'
  return `${days} days ago`
}

function Stat({
  label,
  value,
  tone,
  note,
}: {
  label: string
  value: string
  tone?: 'in' | 'out'
  note?: string
}) {
  const color =
    tone === 'in'
      ? 'text-[var(--fl-hero-income)]'
      : tone === 'out'
        ? 'text-[var(--fl-hero-expense)]'
        : ''
  return (
    <div>
      <p className="text-[11px] text-[var(--fl-hero-fg-2)] lg:text-xs">{label}</p>
      <p className={`font-serif text-sm font-semibold tabular-nums md:text-xl lg:mt-0.5 ${color}`}>
        {value}
      </p>
      {note && <p className="text-[11px] text-[var(--fl-hero-fg-2)]">{note}</p>}
    </div>
  )
}

function StatGrid({ children, cols }: { children: React.ReactNode; cols: 2 | 3 }) {
  return (
    <div
      className={`mt-2.5 grid gap-2 border-t border-[var(--fl-hero-bar-faint)] pt-2 lg:mt-4 lg:gap-3 lg:pt-3 ${cols === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}
    >
      {children}
    </div>
  )
}

/** The account's headline figure: stacked and large on desktop, one line beside its label on a phone. */
function HeroFigure({
  label,
  tone,
  wide,
  children,
}: {
  label: string
  tone?: 'out'
  wide: boolean
  children: React.ReactNode
}) {
  const color = tone === 'out' ? 'text-[var(--fl-hero-expense)]' : ''
  if (wide) {
    return (
      <>
        <p className="mt-4 text-[13px] text-[var(--fl-hero-fg-2)]">{label}</p>
        <p
          data-testid="hero-amount"
          className={`mt-0.5 font-serif text-[38px] leading-tight font-semibold tracking-tight tabular-nums md:text-[42px] ${color}`}
        >
          {children}
        </p>
      </>
    )
  }
  return (
    <div className="mt-2.5 flex items-baseline justify-between gap-3">
      <p className="text-[11px] font-semibold tracking-[0.08em] text-[var(--fl-hero-fg-2)] uppercase">
        {label}
      </p>
      <p
        data-testid="hero-amount"
        className={`font-serif text-2xl leading-tight font-bold tabular-nums ${color}`}
      >
        {children}
      </p>
    </div>
  )
}

export function AccountHero({
  account,
  owners,
  earmarks,
  monthTotals,
  latestValuation,
  menu,
  actions,
  preview,
}: {
  account: AccountSummary
  /** A short owners line, or null when every member owns it (About lists them in full). */
  owners: string | null
  earmarks?: EarmarkResponse
  /** This month's spent and income on the account; null until loaded or when not shown. */
  monthTotals: { spent: number; income: number } | null
  /** Undefined while loading; null when no value has been recorded. */
  latestValuation?: Valuation | null
  menu?: React.ReactNode
  actions?: React.ReactNode
  /** A small trend line under the headline figure (phones). */
  preview?: React.ReactNode
}) {
  const { format: exact, formatWhole } = useFormatCurrency()
  const wide = useMediaQuery(WIDE_QUERY)
  // A phone hero is a glance, like the Dashboard and Accounts: whole rupees.
  const format = wide ? exact : formatWhole
  const today = new Date()
  const subtitle = [
    TYPE_LABELS[account.type],
    account.subType && subTypeLabel(account.subType),
    owners,
  ]
    .filter(Boolean)
    .join(' · ')

  let body: React.ReactNode
  if (account.type === 'CREDIT_CARD') {
    const owed = Math.abs(account.balance)
    const used = account.creditLimit ? owed / account.creditLimit : null
    const bill = nextBillDate(account.billingDate, today)
    body = (
      <>
        <HeroFigure label="To pay" tone="out" wide={wide}>
          {format(owed)}
        </HeroFigure>
        {preview}
        {used !== null && (
          <>
            <div
              aria-hidden="true"
              className="mt-2 h-1.5 overflow-hidden rounded-full bg-[var(--fl-hero-bar-faint)] lg:mt-3 lg:h-2.5"
            >
              <div
                className="h-full rounded-full bg-[var(--fl-hero-expense)] opacity-80"
                style={{ width: `${Math.round(Math.min(used, 1) * 100)}%` }}
              />
            </div>
            <p className="mt-1 text-xs text-[var(--fl-hero-fg-2)] lg:mt-1.5 lg:text-[13px]">
              {Math.round(used * 100)}% of your limit used
            </p>
          </>
        )}
        <StatGrid cols={3}>
          <Stat
            label="Limit"
            value={account.creditLimit ? format(account.creditLimit) : 'Not set'}
          />
          <Stat
            label="Available"
            value={format(account.availableCredit ?? 0)}
            tone={account.availableCredit !== null ? 'in' : undefined}
          />
          <Stat
            label="Bill on"
            value={bill ? bill.toLocaleDateString('en-IN', SHORT_DATE) : 'Not set'}
            note={bill ? daysLabel(daysUntil(bill, today)) : undefined}
          />
        </StatGrid>
      </>
    )
  } else if (account.type === 'INVESTMENT' || account.type === 'PROPERTY') {
    const gain = investmentGain(account)
    const worth = gain ? account.currentValue! : account.balance
    body = (
      <>
        <HeroFigure label="Worth today" wide={wide}>
          {format(worth)}
        </HeroFigure>
        {gain && (
          <p
            className={`mt-1 text-sm font-semibold tabular-nums ${gain.amount >= 0 ? 'text-[var(--fl-hero-income)]' : 'text-[var(--fl-hero-expense)]'}`}
          >
            {gain.amount >= 0 ? '+' : '−'}
            {format(Math.abs(gain.amount))}
            {gain.pct !== null && ` · ${Math.abs(gain.pct).toFixed(1)}%`}{' '}
            {gain.amount >= 0 ? 'gain' : 'loss'}
          </p>
        )}
        <StatGrid cols={2}>
          <Stat
            label={account.type === 'PROPERTY' ? 'Bought for' : 'You put in'}
            value={format(account.balance)}
          />
          {gain && (
            <Stat
              label={gain.amount >= 0 ? 'Gain' : 'Loss'}
              value={`${gain.amount >= 0 ? '+' : '−'}${format(Math.abs(gain.amount))}`}
              tone={gain.amount >= 0 ? 'in' : 'out'}
            />
          )}
        </StatGrid>
        {latestValuation !== undefined && (
          <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[var(--fl-hero-bar-faint)] lg:mt-3 px-2.5 py-1 text-xs text-[var(--fl-hero-fg-2)]">
            <Clock size={12} aria-hidden="true" />
            {latestValuation
              ? `Value updated ${formatShortDate(latestValuation.asOfDate)} · ${agoLabel(
                  daysUntil(today, new Date(`${latestValuation.asOfDate}T00:00:00`))
                )}`
              : 'No value recorded yet'}
          </p>
        )}
      </>
    )
  } else {
    const owing = account.type === 'PAYABLE'
    const set = earmarks && earmarks.earmarked > 0 ? earmarks : null
    body = (
      <>
        <HeroFigure
          label={owing ? 'You owe' : account.type === 'RECEIVABLE' ? 'Owed to you' : 'Balance'}
          tone={owing ? 'out' : undefined}
          wide={wide}
        >
          {format(owing ? Math.abs(account.balance) : account.balance)}
        </HeroFigure>
        {preview}
        {set && (
          <>
            <div
              aria-hidden="true"
              className="mt-2 flex h-1.5 gap-0.5 overflow-hidden rounded lg:mt-3 lg:h-2"
            >
              {set.available > 0 && (
                <div className="bg-[var(--fl-hero-bar)]" style={{ flex: set.available }} />
              )}
              <div className="bg-[var(--fl-warning-text)]" style={{ flex: set.earmarked }} />
            </div>
            <p className="mt-1 text-xs text-[var(--fl-hero-fg-2)] lg:mt-1.5 lg:text-[13px]">
              <span className="font-semibold text-[var(--fl-hero-fg)]">
                {format(set.available)} free
              </span>
              {' · '}
              {format(set.earmarked)} set aside for {set.goals.length}{' '}
              {set.goals.length === 1 ? 'goal' : 'goals'}
            </p>
          </>
        )}
        {monthTotals && (
          <StatGrid cols={2}>
            <Stat label="Income this month" value={format(monthTotals.income)} tone="in" />
            <Stat label="Spent this month" value={format(monthTotals.spent)} tone="out" />
          </StatGrid>
        )}
      </>
    )
  }

  return (
    <section
      aria-label={account.name}
      className="rounded-2xl border border-[var(--fl-hero-border)] bg-gradient-to-br from-[var(--fl-hero-bg)] to-[var(--fl-hero-bg-2)] px-3.5 py-3 text-[var(--fl-hero-fg)] sm:rounded-[20px] sm:p-6 lg:grid lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] lg:gap-8"
    >
      <div>
        <div className="flex items-center gap-3">
          <CategoryIcon
            icon={account.icon || getDefaultAccountIcon(account.type, account.subType)}
            size={wide ? 20 : 16}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-[var(--fl-hero-bar-faint)] text-[var(--fl-hero-fg)] sm:h-11 sm:w-11 sm:rounded-[13px]"
          />
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-serif text-[17px] font-semibold sm:text-[22px] md:text-[26px]">
              {account.name}
            </h1>
            <p className="truncate text-xs text-[var(--fl-hero-fg-2)] md:text-[13px]">{subtitle}</p>
          </div>
          {menu}
        </div>
        {body}
      </div>
      {actions && <div className="mt-3 lg:mt-0 lg:self-end">{actions}</div>}
    </section>
  )
}
